import { supabase } from '../lib/supabaseClient'
import { TokenTransaction, User } from '../types/database'

// Constante de conversão: 10 tokens = 1 Real (valor unitário = R$ 0,10)
export const TOKEN_TO_BRL_RATE = 10

// Cache para armazenar resultados temporariamente
interface CacheItem<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

const cache: {
  balances: Record<string, CacheItem<number>>;
  transactions: Record<string, CacheItem<TokenTransaction[]>>;
} = {
  balances: {},
  transactions: {}
};

// Tempo de expiração do cache em ms (5 minutos)
const CACHE_TTL = 300000;
const LEDGER_WINDOW_DAYS = 90;

// Controle de requisições em andamento para evitar duplicações
const pendingRequests: {
  balances: Record<string, Promise<number>>;
  transactions: Record<string, Promise<TokenTransaction[]>>;
} = {
  balances: {},
  transactions: {}
};

// Limpar cache
export const clearTokenCache = () => {
  cache.balances = {};
  cache.transactions = {};
};

// Converter tokens para reais
export const tokensToReais = (tokens: number): number => {
  return tokens / TOKEN_TO_BRL_RATE
}

// Converter reais para tokens
export const reaisToTokens = (reais: number): number => {
  return reais * TOKEN_TO_BRL_RATE
}

// Obter saldo de tokens do usuário com cache e debounce
export const getUserTokenBalance = async (userId: string): Promise<number> => {
  // Se não tiver userId, retorna 0
  if (!userId) return 0;
  const offline = typeof navigator !== 'undefined' && navigator && navigator.onLine === false
  const hidden = typeof document !== 'undefined' && (document as any)?.hidden === true
  
  // Verificar se há resultado em cache válido
  const cachedBalance = cache.balances[userId];
  if (cachedBalance && cachedBalance.expiresAt > Date.now()) {
    console.log('📊 [TokenService] Usando saldo em cache para', userId);
    return cachedBalance.data;
  }
  
  // Verificar se já existe uma requisição em andamento para este usuário
  if (pendingRequests.balances[userId]) {
    console.log('⏳ [TokenService] Reutilizando requisição de saldo em andamento para', userId);
    try {
      return await pendingRequests.balances[userId];
    } catch (error) {
      console.error('❌ [TokenService] Erro na requisição reutilizada:', error);
      return 0;
    }
  }
  
  // Criar nova requisição
  const requestPromise = (async () => {
    try {
      if (offline || hidden) {
        const cached = cache.balances[userId]
        return cached ? cached.data : 0
      }
      const { data, error } = await supabase
        .from('users')
        .select('token_balance')
        .eq('uid', userId)
        .single();

      if (error) {
        console.error('❌ [TokenService] Erro ao obter saldo de tokens:', error);
        return 0;
      }

      const balance = data?.token_balance || 0;
      
      // Armazenar no cache
      cache.balances[userId] = {
        data: balance,
        timestamp: Date.now(),
        expiresAt: Date.now() + CACHE_TTL
      };
      
      return balance;
    } catch (error) {
      console.error('❌ [TokenService] Erro ao obter saldo de tokens:', error);
      return 0;
    } finally {
      // Remover da lista de requisições pendentes
      delete pendingRequests.balances[userId];
    }
  })();
  
  // Registrar a requisição pendente
  pendingRequests.balances[userId] = requestPromise;
  
  return requestPromise;
}

// Adicionar tokens ao saldo do usuário
export const addTokensToUser = async (
  userId: string,
  amount: number,
  description: string,
  type: 'purchase' | 'bonus' | 'refund' = 'purchase',
  relatedId?: string
): Promise<void> => {
  try {
    // Usar a função RPC correta do Supabase
    const { data, error } = await supabase.rpc('credit_user_tokens', {
      p_user_uid: userId,
      p_amount: amount,
      p_reason: description,
      p_metadata: {
        type: type,
        related_id: relatedId || null
      }
    });

    if (error) {
      console.error('Erro ao creditar tokens:', error);
      throw error;
    }

    if (!data) {
      throw new Error('Falha ao creditar tokens');
    }

    console.log(`✅ Tokens creditados com sucesso: ${amount} para usuário ${userId}`);
  } catch (error) {
    console.error('Erro ao adicionar tokens ao usuário:', error);
    throw error;
  }
}

// Gastar tokens do usuário
export const spendUserTokens = async (
  userId: string,
  amount: number,
  description: string,
  relatedId?: string
): Promise<boolean> => {
  try {
    const { data, error } = await supabase.rpc('debit_user_tokens', {
      p_user_uid: userId,
      p_amount: amount,
      p_reason: description,
      p_metadata: {
        related_id: relatedId || null
      }
    });

    if (error) {
      console.error('Erro ao debitar tokens:', error);
      throw error;
    }

    console.log(`✅ Tokens debitados: ${amount} do usuário ${userId}`);
    return data; // Retorna true se a operação foi bem-sucedida, false caso contrário (ex: saldo insuficiente)
  } catch (error) {
    console.error('Erro ao gastar tokens do usuário:', error);
    throw error;
  }
}

// Obter histórico de transações de tokens com cache e debounce
export const getUserTokenTransactions = async (
  userId: string,
  limitCount: number = 20
): Promise<TokenTransaction[]> => {
  // Se não tiver userId, retorna array vazio
  if (!userId) return [];
  
  // Chave de cache que inclui o limite para diferenciar consultas
  const cacheKey = `${userId}_${limitCount}`;
  
  // Verificar se há resultado em cache válido
  const cachedTransactions = cache.transactions[cacheKey];
  if (cachedTransactions && cachedTransactions.expiresAt > Date.now()) {
    console.log('📊 [TokenService] Usando transações em cache para', userId);
    return cachedTransactions.data;
  }
  
  // Verificar se já existe uma requisição em andamento para este usuário
  if (pendingRequests.transactions[cacheKey]) {
    console.log('⏳ [TokenService] Reutilizando requisição de transações em andamento para', userId);
    try {
      return await pendingRequests.transactions[cacheKey];
    } catch (error) {
      console.error('❌ [TokenService] Erro na requisição reutilizada:', error);
      return [];
    }
  }
  
  // Criar nova requisição
  const requestPromise = (async () => {
    try {
      const since = new Date(Date.now() - LEDGER_WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from('token_ledger')
        .select('id,user_uid,amount,transaction_type,description,reason,balance_after,payment_id,payment_amount,user_email,metadata,created_at')
        .eq('user_uid', userId)
        .gte('created_at', since)
        .order('created_at', { ascending: false })
        .limit(limitCount);
      let rows = data || []
      if (error) {
        const msg = String(error?.message || '')
        const isTimeout = (error?.code === '57014') || /statement timeout/i.test(msg)
        const isSchemaCache = String(error?.code || '').toUpperCase() === 'PGRST002'
        if (isTimeout) {
          try {
            const safeLimit = Math.max(10, Math.floor(limitCount / 2))
            const sinceFb = new Date(Date.now() - LEDGER_WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString();
            const { data: fallback, error: fbErr } = await supabase
              .from('token_ledger')
              .select('id,user_uid,amount,transaction_type,description,reason,balance_after,payment_id,payment_amount,user_email,metadata,created_at')
              .eq('user_uid', userId)
              .gte('created_at', sinceFb)
              .order('created_at', { ascending: false })
              .limit(safeLimit)
            if (!fbErr && fallback) {
              rows = fallback
            } else {
              console.warn('⚠️ [TokenService] Timeout ao obter histórico; fallback falhou:', fbErr)
              return []
            }
          } catch (e) {
            console.warn('⚠️ [TokenService] Erro no fallback de timeout:', e)
            return []
          }
        } else if (isSchemaCache) {
          try {
            await new Promise(r => setTimeout(r, 800))
            const { data: retry, error: rErr } = await supabase
              .from('token_ledger')
              .select('id,user_uid,amount,transaction_type,description,reason,balance_after,payment_id,payment_amount,user_email,metadata,created_at')
              .eq('user_uid', userId)
              .gte('created_at', since)
              .order('created_at', { ascending: false })
              .limit(limitCount)
            if (!rErr && retry) {
              rows = retry
            } else {
              return []
            }
          } catch {
            return []
          }
        } else {
          console.error('❌ [TokenService] Erro ao obter histórico de transações:', error)
          return []
        }
      }

      const transactions = (rows || []).map(tx => ({
        id: tx.id,
        user_uid: tx.user_uid,
        amount: Number(tx.amount),
        balance_after: Number(tx.balance_after ?? 0),
        transaction_type: String(tx.transaction_type || ''),
        description: tx.description ?? undefined,
        reason: tx.reason ?? undefined,
        payment_id: tx.payment_id ?? undefined,
        payment_amount: tx.payment_amount ?? undefined,
        user_email: tx.user_email ?? undefined,
        metadata: tx.metadata,
        created_at: tx.created_at,
      })) as TokenTransaction[];
    
    // Armazenar no cache
    cache.transactions[cacheKey] = {
      data: transactions,
      timestamp: Date.now(),
      expiresAt: Date.now() + CACHE_TTL
    };
    
    return transactions;
  } catch (error) {
    console.warn('⚠️ [TokenService] Erro ao carregar histórico de transações:', error);
    return [];
  } finally {
    // Remover da lista de requisições pendentes
    delete pendingRequests.transactions[cacheKey];
  }
})();

// Registrar a requisição pendente
pendingRequests.transactions[cacheKey] = requestPromise;

return requestPromise;
}

// Criar intenção de compra de tokens
export const createTokenPurchase = async (
  userId: string,
  tokenAmount: number,
  reaisAmount: number
): Promise<string> => {
  try {
    const purchaseId = crypto.randomUUID()
    const { data, error } = await supabase
      .from('purchase_intents')
      .insert({
        user_uid: userId,
        purchase_id: purchaseId,
        amount: reaisAmount,
        tokens_to_credit: tokenAmount,
        status: 'pending',
        payment_method: 'pix', // Default, pode ser alterado depois
        metadata: { source: 'tokenService' }
      })
      .select('id')
      .single()

    if (error) {
      console.error('Erro ao criar intenção de compra:', error)
      throw error
    }

    // Retorna o ID interno da tabela (UUID) ou o purchase_id (string) dependendo do que o chamador espera
    // O código antigo esperava data.id. Vamos retornar data.id (UUID do banco)
    return data.id
  } catch (error) {
    console.error('Erro ao criar intenção de compra:', error)
    throw error
  }
}

// Aprovar compra de tokens (Atualiza purchase_intents)
export const approveTokenPurchase = async (
  purchaseId: string
): Promise<void> => {
  try {
    // Nota: A aprovação real deve ser feita via webhook ou backend seguro.
    // Esta função apenas atualiza o status visualmente se necessário.
    const { error } = await supabase
      .from('purchase_intents')
      .update({
        status: 'completed',
        updated_at: new Date().toISOString()
      })
      .eq('id', purchaseId) // Assumindo que purchaseId é o UUID da linha

    if (error) {
      console.error('Erro ao aprovar compra de tokens:', error)
      throw error
    }
  } catch (error) {
    console.error('Erro ao aprovar compra de tokens:', error)
    throw error
  }
}

// Rejeitar compra de tokens
export const rejectTokenPurchase = async (
  purchaseId: string,
  reason?: string
): Promise<void> => {
  try {
    const { error } = await supabase
      .from('purchase_intents')
      .update({
        status: 'cancelled',
        updated_at: new Date().toISOString(),
        metadata: { rejection_reason: reason }
      })
      .eq('id', purchaseId)

    if (error) {
      console.error('Erro ao rejeitar compra de tokens:', error)
      throw error
    }
  } catch (error) {
    console.error('Erro ao rejeitar compra de tokens:', error)
    throw error
  }
}

// Registrar transação de tokens (Wrapper para UnifiedTokenService ou RPC direto)
export const recordTokenTransaction = async (
  userId: string,
  amount: number,
  type: 'credit' | 'debit',
  description: string,
  relatedId?: string
): Promise<TokenTransaction> => {
  try {
    // Usar RPC atômico para garantir consistência
    const rpcName = type === 'credit' ? 'credit_user_tokens' : 'debit_user_tokens'
    const { data, error } = await supabase.rpc(rpcName, {
      p_user_uid: userId,
      p_amount: Math.abs(amount),
      p_reason: description,
      p_metadata: { related_id: relatedId, manual_record: true }
    })

    if (error) throw error

    // Retornar um objeto compatível com TokenTransaction simulado
    // (O RPC retorna apenas true/false ou novo saldo, não a transação completa)
    // Para obter a transação real, teríamos que consultar o ledger.
    // Simplificação: retornar objeto mockado com dados básicos
    return {
      id: Date.now(), // ID temporário
      user_uid: userId,
      amount: type === 'debit' ? -Math.abs(amount) : Math.abs(amount),
      transaction_type: type,
      description,
      metadata: { related_id: relatedId },
      created_at: new Date().toISOString(),
      balance_after: 0 // Desconhecido sem consulta extra
    } as TokenTransaction
  } catch (error) {
    console.error('Erro ao registrar transação de tokens:', error)
    throw error
  }
}